import { useState, useEffect, useCallback } from "react";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import Modal from "../../components/ui/Modal";
import Badge from "../../components/ui/Badge";
import { lookupsApi } from "../../api/lookups";
import { invalidateLookupCache } from "../../hooks/useLookups";

const CATEGORIES = [
  { key: "machine_type", label: "Machine Types" },
  { key: "meco_type",    label: "MECO Types" },
];

const COLORS = ["blue", "green", "yellow", "red", "gray", "purple"];

const EMPTY_MACHINE = { label: "", color: "gray", sort_order: 0, is_active: true };

// ── Color picker ─────────────────────────────────────────────────────────────

function ColorSelect({ value, onChange }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {COLORS.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={`rounded-full transition-all ${
            value === c ? "ring-2 ring-offset-1 ring-bh-500 scale-110" : "opacity-60 hover:opacity-100"
          }`}
        >
          <Badge color={c}>{c}</Badge>
        </button>
      ))}
    </div>
  );
}

// ── Create modal (machine types only) ────────────────────────────────────────

function CreateMachineTypeModal({ open, onClose, onCreated }) {
  const [form,   setForm]   = useState(EMPTY_MACHINE);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  useEffect(() => { if (open) { setForm(EMPTY_MACHINE); setError(null); } }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const { data } = await lookupsApi.createMachineType({ ...form, sort_order: Number(form.sort_order) });
      invalidateLookupCache("machine_type");
      onCreated(data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add machine type">
      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block">
          <span className="text-xs font-medium text-gray-500 block mb-1">Label *</span>
          <input value={form.label} onChange={set("label")} required
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-bh-400" />
        </label>
        <div>
          <span className="text-xs font-medium text-gray-500 block mb-2">Badge color</span>
          <ColorSelect value={form.color} onChange={(c) => setForm((f) => ({ ...f, color: c }))} />
        </div>
        <label className="block">
          <span className="text-xs font-medium text-gray-500 block mb-1">Sort order</span>
          <input type="number" value={form.sort_order} onChange={set("sort_order")}
            className="w-24 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-bh-400" />
        </label>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={saving}
            className="px-4 py-2 bg-bh-600 text-white rounded-lg text-sm font-medium hover:bg-bh-700 disabled:opacity-50 transition-colors">
            {saving ? "Creating…" : "Create"}
          </button>
          <button type="button" onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Edit modal ────────────────────────────────────────────────────────────────

function EditModal({ item, isMeco, open, onClose, onUpdated }) {
  const [form,   setForm]   = useState({});
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  useEffect(() => {
    if (open && item) {
      setForm(isMeco
        ? { color: item.color }
        : { label: item.label, color: item.color, sort_order: item.sort_order, is_active: item.is_active });
      setError(null);
    }
  }, [open, item, isMeco]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const payload = isMeco
        ? { color: form.color }
        : { ...form, sort_order: Number(form.sort_order) };
      const apiFn = isMeco ? lookupsApi.updateMecoType : lookupsApi.updateMachineType;
      const { data } = await apiFn(item.id, payload);
      invalidateLookupCache(isMeco ? "meco_type" : "machine_type");
      onUpdated(data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!item) return null;

  return (
    <Modal open={open} onClose={onClose} title={`Edit — ${item.label}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {!isMeco && (
          <label className="block">
            <span className="text-xs font-medium text-gray-500 block mb-1">Label *</span>
            <input value={form.label ?? ""} onChange={set("label")} required
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-bh-400" />
          </label>
        )}
        <div>
          <span className="text-xs font-medium text-gray-500 block mb-2">Badge color</span>
          <ColorSelect value={form.color} onChange={(c) => setForm((f) => ({ ...f, color: c }))} />
        </div>
        {!isMeco && (
          <div className="flex items-center gap-6">
            <label className="block">
              <span className="text-xs font-medium text-gray-500 block mb-1">Sort order</span>
              <input type="number" value={form.sort_order ?? 0} onChange={set("sort_order")}
                className="w-24 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-bh-400" />
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none mt-4">
              <input type="checkbox" checked={!!form.is_active} onChange={set("is_active")}
                className="rounded border-gray-300 accent-bh-600" />
              Active
            </label>
          </div>
        )}
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={saving}
            className="px-4 py-2 bg-bh-600 text-white rounded-lg text-sm font-medium hover:bg-bh-700 disabled:opacity-50 transition-colors">
            {saving ? "Saving…" : "Save changes"}
          </button>
          <button type="button" onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AdminLookupTypesPage() {
  const [activeCategory, setActiveCategory] = useState("machine_type");
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const isMeco = activeCategory === "meco_type";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const apiFn = isMeco ? lookupsApi.adminListMecoTypes : lookupsApi.adminListMachineTypes;
      const { data } = await apiFn();
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isMeco]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (t) => setItems((prev) => [...prev, t].sort((a, b) => a.sort_order - b.sort_order));
  const handleUpdated = (t) => setItems((prev) => prev.map((x) => (x.id === t.id ? t : x)));

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.label}"? This may break existing records that use this value.`)) return;
    try {
      await lookupsApi.removeMachineType(t.id);
      invalidateLookupCache("machine_type");
      setItems((prev) => prev.filter((x) => x.id !== t.id));
    } catch (err) {
      alert(err?.response?.data?.error || "Delete failed");
    }
  };

  return (
    <div className="p-6 max-w-4xl space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Lookup Types</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage dropdown options for projects and blade designs.</p>
        </div>
        {!isMeco && (
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-bh-600 text-white rounded-lg text-sm font-medium hover:bg-bh-700 transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Add type
          </button>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-gray-200">
        {CATEGORIES.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveCategory(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeCategory === key
                ? "border-bh-600 text-bh-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {isMeco && (
        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          MECO types are fixed — only the badge color can be changed.
        </p>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-8">ID</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Label</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Badge</th>
                {!isMeco && <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Order</th>}
                {!isMeco && <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>}
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.length === 0 && (
                <tr>
                  <td colSpan={isMeco ? 4 : 6} className="px-4 py-8 text-center text-sm text-gray-400">
                    No types yet.
                  </td>
                </tr>
              )}
              {items.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-gray-400">{t.id}</td>
                  <td className="px-4 py-3 text-gray-900">{t.label}</td>
                  <td className="px-4 py-3"><Badge color={t.color}>{t.label}</Badge></td>
                  {!isMeco && <td className="px-4 py-3 text-gray-500 text-xs">{t.sort_order}</td>}
                  {!isMeco && (
                    <td className="px-4 py-3">
                      {t.is_active ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />Disabled
                        </span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setEditTarget(t)}
                        className="p-1.5 rounded text-gray-400 hover:text-bh-600 hover:bg-bh-50 transition-colors">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      {!isMeco && (
                        <button onClick={() => handleDelete(t)}
                          className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateMachineTypeModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
      <EditModal
        item={editTarget}
        isMeco={isMeco}
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        onUpdated={handleUpdated}
      />
    </div>
  );
}
